// Analytics Service - Custom backend tracking (Postgres/Redis version)
import { trackEvent } from './api';

/**
 * Track event to backend analytics
 * @param {string} eventName - The event name
 * @param {object} eventParams - Event parameters
 */
export function trackAnalyticsEvent(eventName, eventParams = {}) {
  // Track to backend analytics
  trackEvent(eventName, eventParams).catch(err => {
    console.warn('Analytics error:', err);
  });
}

// ============================================================
// LANDING PAGE EVENTS
// ============================================================

export function trackLandingPageView() {
  trackAnalyticsEvent('landing_page_view', {
    page: 'landing'
  });
}

export function trackGetStartedClick(source = 'hero') {
  trackAnalyticsEvent('get_started_click', {
    source, // 'hero', 'cta_section', 'header'
    page: 'landing'
  });
}

export function trackFaqExpand(questionIndex, question) {
  trackAnalyticsEvent('faq_expand', {
    question_index: questionIndex,
    question_preview: question.substring(0, 50)
  });
}

export function trackHowItWorksView() {
  trackAnalyticsEvent('how_it_works_view', {
    page: 'landing'
  });
}

export function trackExampleAnimationView() {
  trackAnalyticsEvent('example_animation_view', {
    page: 'landing'
  });
}

// ============================================================
// AUTH EVENTS
// ============================================================

export function trackLoginAttempt(method) {
  trackAnalyticsEvent('login_attempt', {
    method // 'google', 'email'
  });
}

export function trackLoginSuccess(method) {
  trackAnalyticsEvent('login', {
    method
  });
}

export function trackLoginError(method, errorCode) {
  trackAnalyticsEvent('login_error', {
    method,
    error_code: errorCode
  });
}

export function trackSignupAttempt(method) {
  trackAnalyticsEvent('signup_attempt', {
    method
  });
}

export function trackSignupSuccess(method) {
  trackAnalyticsEvent('sign_up', {
    method
  });
}

export function trackSignupError(method, errorCode) {
  trackAnalyticsEvent('signup_error', {
    method,
    error_code: errorCode
  });
}

// ============================================================
// HOME PAGE EVENTS
// ============================================================

export function trackHomePageView() {
  trackAnalyticsEvent('home_page_view', {
    page: 'home'
  });
}

export function trackGoalSubmit(goal) {
  trackAnalyticsEvent('goal_submit', {
    goal_preview: goal.substring(0, 100),
    goal_length: goal.length
  });
}

export function trackExampleSelect(example, category) {
  trackAnalyticsEvent('example_select', {
    example_preview: example.substring(0, 50),
    category
  });
}

export function trackSessionContinue(sessionId, hasStack) {
  trackAnalyticsEvent('session_continue', {
    session_id: sessionId,
    has_stack: hasStack
  });
}

// ============================================================
// CONFIGURE (INTERVIEW) EVENTS
// ============================================================

export function trackInterviewStart(goal) {
  trackAnalyticsEvent('interview_start', {
    goal_preview: goal.substring(0, 100)
  });
}

export function trackInterviewQuestionAnswer(questionId, questionIndex, totalQuestions) {
  trackAnalyticsEvent('interview_question_answer', {
    question_id: questionId,
    question_index: questionIndex,
    total_questions: totalQuestions,
    progress_percent: Math.round((questionIndex / totalQuestions) * 100)
  });
}

export function trackInterviewComplete(goal, totalQuestions) {
  trackAnalyticsEvent('interview_complete', {
    goal_preview: goal.substring(0, 100),
    total_questions: totalQuestions
  });
}

export function trackInterviewAbandoned(questionIndex, totalQuestions) {
  trackAnalyticsEvent('interview_abandoned', {
    question_index: questionIndex,
    total_questions: totalQuestions,
    progress_percent: Math.round((questionIndex / totalQuestions) * 100)
  });
}

// ============================================================
// STACK PAGE EVENTS
// ============================================================

export function trackStackView(toolCount, estimatedCost) {
  trackAnalyticsEvent('stack_view', {
    tool_count: toolCount,
    estimated_cost: estimatedCost
  });
}

export function trackStackApprove(toolCount, estimatedCost) {
  trackAnalyticsEvent('stack_approve', {
    tool_count: toolCount,
    estimated_cost: estimatedCost
  });
}

export function trackStackModify() {
  trackAnalyticsEvent('stack_modify', {});
}

export function trackStackRegenerate(changedFields) {
  trackAnalyticsEvent('stack_regenerate', {
    changed_fields: changedFields.join(',')
  });
}

// ============================================================
// WAITLIST EVENTS
// ============================================================

export function trackWaitlistView() {
  trackAnalyticsEvent('waitlist_view', {
    page: 'waitlist'
  });
}

export function trackWaitlistSubmit(hasStack) {
  trackAnalyticsEvent('waitlist_submit', {
    has_stack: hasStack
  });
}

export function trackWaitlistSuccess(position) {
  trackAnalyticsEvent('waitlist_success', {
    position
  });
}

export function trackWaitlistShare(platform) {
  trackAnalyticsEvent('waitlist_share', {
    platform // 'twitter', 'linkedin', 'copy_link'
  });
}

// ============================================================
// MCP/SETUP EVENTS
// ============================================================

export function trackSetupPageView(source, mcpCount) {
  trackAnalyticsEvent('setup_page_view', {
    source, // 'chatgpt', 'direct'
    mcp_count: mcpCount
  });
}

export function trackTransferClaim(mcpCount) {
  trackAnalyticsEvent('transfer_claim', {
    mcp_count: mcpCount
  });
}

export function trackTransferClaimSuccess(mcpCount) {
  trackAnalyticsEvent('transfer_claim_success', {
    mcp_count: mcpCount
  });
}

// ============================================================
// NAVIGATION EVENTS
// ============================================================

export function trackNavigation(from, to) {
  trackAnalyticsEvent('navigation', {
    from_page: from,
    to_page: to
  });
}

export function trackLogout() {
  trackAnalyticsEvent('logout', {});
}
